import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { X, ArrowRight, Phone } from 'lucide-react';
import { toast } from 'react-toastify';
import { useConvertCallToLeadMutation } from '../../api/callLogsApi';
import { applyApiErrorsToForm } from '../../utils/apiError';
import ApiErrorBanner from '../../components/ApiErrorBanner';

/**
 * Streamlined "Transfer Call to Lead" workflow: strictly two fields —
 * Patient Name + Notes. Everything else is derived from the call log.
 */
export default function ConvertCallToLeadModal({ callLog, onClose }) {
  const { register, handleSubmit, setError, formState: { errors, isSubmitting } } = useForm({
    defaultValues: {
      fullName: callLog?.callerName ?? '',
      notes: '',
    },
  });
  const [convert] = useConvertCallToLeadMutation();
  const [serverError, setServerError] = useState(null);

  if (!callLog) return null;

  const submit = async (data) => {
    setServerError(null);
    try {
      await convert({
        id: callLog.id,
        fullName: data.fullName.trim(),
        notes: data.notes.trim(),
      }).unwrap();
      toast.success(`Lead created from ${callLog.phoneNumberRaw}`);
      onClose();
    } catch (e) {
      const msg = applyApiErrorsToForm(setError, e);
      setServerError(msg);
      toast.error(msg);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
        <form onSubmit={handleSubmit(submit)}>
          <div className="modal-header">
            <h2><ArrowRight size={16} /> Transfer call to lead</h2>
            <button type="button" className="modal-close" onClick={onClose}><X size={18} /></button>
          </div>
          <div className="modal-body">
            <ApiErrorBanner message={serverError} onDismiss={() => setServerError(null)} />

            <div style={{
              padding: '10px 12px',
              background: 'var(--blue-50)',
              border: '1px solid var(--blue-300)',
              borderRadius: 'var(--r-md)',
              fontSize: '0.82rem',
              color: 'var(--blue-800)',
              display: 'flex', alignItems: 'center', gap: 8,
              marginBottom: 12,
            }}>
              <Phone size={14} />
              <span>
                <strong>{callLog.phoneNumberRaw}</strong> ·{' '}
                {callLog.totalCalls} call{callLog.totalCalls !== 1 ? 's' : ''} ·
                first call {new Date(callLog.firstCallAt).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}
              </span>
            </div>

            <div className="form-group">
              <label className="form-label">Patient Name *</label>
              <input
                className="form-control"
                autoFocus
                {...register('fullName', { required: 'Required', maxLength: 128 })}
              />
              {errors.fullName && <span className="form-field-error">{errors.fullName.message}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">Notes *</label>
              <textarea
                className="form-control"
                rows={4}
                placeholder="e.g., Knee pain for 2 weeks, looking for physiotherapy"
                {...register('notes', { required: 'Required', maxLength: 2000 })}
              />
              {errors.notes && <span className="form-field-error">{errors.notes.message}</span>}
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-success" disabled={isSubmitting}>
              {isSubmitting ? 'Transferring…' : 'Create Lead'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
